import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { employeeChangePassword } from "../../../core/data/redux/employeesSlice";
import { fetchAll } from "../../../core/data/redux/rolePermissionsSlice";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Preloader from "../../../core/common/loader/preLoader";

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

const schema = Yup.object().shape({
    new_password: Yup.string()
        .required("New password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
            passwordRegex,
            "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character"
        ),
    confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password"), undefined], "Passwords must match")
        .required("Confirm password is required"),
});

interface ChangePasswordProps {
    showModal: any,
    handleModal: () => void;
}

const ChangePassword = (props: ChangePasswordProps) => {

    const { showModal, handleModal } = props;
    const dispatch = useDispatch();
    const employeesState = useSelector((state: any) => state.employeesState);
    const [showLoader, seShowLoader] = useState(false);
    const [passwordVisibility, setPasswordVisibility] = useState({
        password: false,
        confirmPassword: false,
    });
    const [password, setPassword] = useState("");
    const [passwordResponce, setPasswordResponce] = useState({
        passwordResponceText:
            "Use 8 or more characters with a mix of letters, numbers, and symbols.",
        passwordResponceKey: "",
    });
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const onSubmit = (data: any) => {
        dispatch(employeeChangePassword(
            {
                empId: showModal.empId,
                new_password: data?.new_password,
                confirm_password: data?.confirm_password
            }
        ));
    };

    React.useEffect(() => {
        if (employeesState.loading === "fulfilled" && employeesState.action === "employee-change-password") {
            handleModal()
            toast.success('Password changed successfully');
            reset();
        } else {
            toast.error(employeesState.error);
        }
    }, [employeesState.loading]);




    const onChangePassword = (password: string) => {
        setPassword(password);
        if (password.match(/^$|\s+/)) {
            setPasswordResponce({
                passwordResponceText:
                    "Use 8 or more characters with a mix of letters, numbers & symbols",
                passwordResponceKey: "",
            });
        } else if (password.length === 0) {
            setPasswordResponce({
                passwordResponceText: "",
                passwordResponceKey: "",
            });
        } else if (password.length < 8) {
            setPasswordResponce({
                passwordResponceText: "Weak. Must contain at least 8 characters",
                passwordResponceKey: "0",
            });
        } else if (
            password.search(/[a-z]/) < 0 ||
            password.search(/[A-Z]/) < 0 ||
            password.search(/[0-9]/) < 0
        ) {
            setPasswordResponce({
                passwordResponceText:
                    "Average. Must contain at least 1 upper case and number",
                passwordResponceKey: "1",
            });
        } else if (password.search(/(?=.*?[#?!@$%^&*-])/) < 0) {
            setPasswordResponce({
                passwordResponceText: "Almost. Must contain a special symbol",
                passwordResponceKey: "2",
            });
        } else {
            setPasswordResponce({
                passwordResponceText: "Awesome! You have a secure password.",
                passwordResponceKey: "3",
            });
        }
    };

    const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
        setPasswordVisibility((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };






    return (
        <>
        {(employeesState.loading === "pending" && employeesState.action === "employee-change-password") && <Preloader/>}
            <Modal
                open={showModal?.show}
                onCancel={() => {
                    handleModal();
                    reset();
                }}
                title=""
                footer={false}
            >
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{ margin: "0 auto", marginTop: 20 }}
                >

                    <div className="text-center mb-5">
                        <h2 className="mb-2">Change Password</h2>
                    </div>
                    <div>
                        <div className="input-block mb-3">
                            <div className="mb-3">
                                <label className="form-label">Password</label>
                                <div className="pass-group" id="passwordInput">
                                    <input
                                        type={
                                            passwordVisibility.password ? "text" : "password"
                                        }
                                        className="form-control pass-input"
                                        placeholder="Enter your new password"
                                        {...register("new_password")}
                                        onChange={(e) => onChangePassword(e.target.value)}
                                    />
                                    <span
                                        className={`ti toggle-passwords ${passwordVisibility.password
                                            ? "ti-eye"
                                            : "ti-eye-off"
                                            }`}
                                        onClick={() => togglePasswordVisibility("password")}
                                        style={{ cursor: "pointer" }}
                                    ></span>
                                </div>
                            </div>
                            {errors.new_password && (
                                <small className="text-danger align-items-left">
                                    {errors.new_password.message}
                                </small>
                            )}
                            <div
                                className={`password-strength d-flex ${passwordResponce.passwordResponceKey === "0"
                                    ? "poor-active"
                                    : passwordResponce.passwordResponceKey === "1"
                                        ? "avg-active"
                                        : passwordResponce.passwordResponceKey === "2"
                                            ? "strong-active"
                                            : passwordResponce.passwordResponceKey === "3"
                                                ? "heavy-active"
                                                : ""
                                    }`}
                                id="passwordStrength"
                            >
                                <span id="poor" className="active" />
                                <span id="weak" className="active" />
                                <span id="strong" className="active" />
                                <span id="heavy" className="active" />
                            </div>
                        </div>
                        <p className="fs-12">
                            {passwordResponce.passwordResponceText}
                        </p>
                        <div className="mb-3">
                            <label className="form-label">Confirm Password</label>
                            <div className="pass-group">
                                <input
                                    type={
                                        passwordVisibility.confirmPassword
                                            ? "text"
                                            : "password"
                                    }
                                    className="pass-input form-control"
                                    {...register("confirm_password")}
                                />
                                <span
                                    className={`ti toggle-passwords ${passwordVisibility.confirmPassword
                                        ? "ti-eye"
                                        : "ti-eye-off"
                                        }`}
                                    onClick={() =>
                                        togglePasswordVisibility("confirmPassword")
                                    }
                                ></span>
                            </div>
                            {errors.confirm_password && (
                                <small className="text-danger align-items-left">
                                    {errors.confirm_password.message}
                                </small>
                            )}
                        </div>
                        <div className="mb-3">
                            <button type="submit" className="btn btn-info w-100">
                                Submit
                            </button>
                        </div>
                    </div>

                </form>
            </Modal>
        </>
    );
}

export default ChangePassword; 